import React, { useState, useEffect, useCallback } from 'react';
import {
  Package, Search, Loader2, RefreshCw, ChevronLeft, ChevronRight,
  ChevronDown, ChevronRight as ChevronR, Link2,
  CheckCircle, Truck, CreditCard
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService, KeapOrder, KeapOrderDetail, KeapOrderStats } from '../services/api';

const fmtCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const fmtDate = (d?: string) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const KeapOrders: React.FC = () => {
  const [stats, setStats] = useState<KeapOrderStats | null>(null);
  const [orders, setOrders] = useState<KeapOrder[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [matchFilter, setMatchFilter] = useState('');
  const [sortBy, setSortBy] = useState('order_date');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [matching, setMatching] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<KeapOrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadStats = useCallback(async () => {
    const data = await apiService.getKeapOrderStats();
    setStats(data);
  }, []);

  const loadOrders = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const result = await apiService.getKeapOrders({
        page, limit: 50, search: search || undefined,
        type: typeFilter || undefined, matchStatus: matchFilter || undefined,
        sortBy, sortDir,
      });
      setOrders(result.data);
      setPagination(result.pagination);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, matchFilter, sortBy, sortDir]);

  // Search only applies on Enter key — no auto-apply

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadOrders(); }, [loadOrders]);

  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await apiService.syncKeapOrders(7);
      if (result) {
        toast.success(`Synced: ${result.created} new, ${result.updated} updated`);
        loadStats();
        loadOrders();
      }
    } catch { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  const handleMatch = async () => {
    setMatching(true);
    try {
      const result = await apiService.runKeapOrderMatching();
      if (result) {
        toast.success(
          `Matched: ${result.transactions?.matched || 0} transactions, ${result.callaway?.matched || 0} Callaway, ${result.customersLinked || 0} customers`
        );
        loadStats();
        loadOrders();
      }
    } catch { toast.error('Matching failed'); }
    finally { setMatching(false); }
  };

  const toggleDetail = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(id);
    setDetailLoading(true);
    const data = await apiService.getKeapOrder(id);
    setDetail(data);
    setDetailLoading(false);
  };

  const SortTh: React.FC<{ col: string; label: string; className?: string }> = ({ col, label, className }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none ${className || ''}`}
      onClick={() => handleSort(col)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortBy === col && <span className="text-blue-500">{sortDir === 'asc' ? '▲' : '▼'}</span>}
      </div>
    </th>
  );

  const MatchBadge: React.FC<{ method?: string; confidence?: number; linked: boolean }> = ({ method, confidence, linked }) => {
    if (!linked) return <span className="text-xs text-gray-400">—</span>;
    const pct = confidence ? Math.round(confidence * 100) : 0;
    const color = pct >= 90 ? 'bg-green-50 text-green-700' : pct >= 70 ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-700';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
        <CheckCircle className="h-3 w-3 mr-1" />{method} {pct}%
      </span>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Club & Trade-In Orders</h1>
          <p className="text-sm text-gray-500 mt-1">Keap orders with three-way linking to transactions and Callaway invoices</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleSync} disabled={syncing}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {syncing ? 'Syncing...' : 'Sync Orders'}
          </button>
          <button onClick={handleMatch} disabled={matching}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">
            {matching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            {matching ? 'Matching...' : 'Run Matching'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Club Orders</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.totalClubOrders?.toLocaleString() || 0}</p>
          <p className="text-xs text-gray-400 mt-1">{fmtCurrency(stats?.totalClubRevenue || 0)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Trade-In Orders</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.totalTradeInOrders?.toLocaleString() || 0}</p>
          <p className="text-xs text-gray-400 mt-1">{fmtCurrency(stats?.totalTradeInRevenue || 0)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Unmatched to Transaction</p>
          <p className={`text-2xl font-bold mt-1 ${(stats?.unmatchedToTransaction || 0) > 0 ? 'text-orange-600' : 'text-green-700'}`}>
            {stats?.unmatchedToTransaction || 0}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Unmatched to Callaway</p>
          <p className={`text-2xl font-bold mt-1 ${(stats?.unmatchedToCallaway || 0) > 0 ? 'text-orange-600' : 'text-green-700'}`}>
            {stats?.unmatchedToCallaway || 0}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-100 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" placeholder="Search by name, email, or order ID..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setSearch(searchInput); }}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All Types</option>
            <option value="club">Club Orders</option>
            <option value="trade_in">Trade-In Orders</option>
          </select>
          <select value={matchFilter} onChange={(e) => setMatchFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All Match Status</option>
            <option value="unmatched_tx">Unmatched to Transaction</option>
            <option value="unmatched_callaway">Unmatched to Callaway</option>
            <option value="fully_matched">Fully Matched</option>
          </select>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-8 px-4 py-3"></th>
                <SortTh col="keap_order_id" label="Order" />
                <SortTh col="keap_contact_id" label="Contact ID" />
                <SortTh col="contact_full_name" label="Customer" />
                <SortTh col="order_type" label="Type" />
                <SortTh col="total_paid" label="Amount" className="text-right" />
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Merchant</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Transaction</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Callaway</th>
                <SortTh col="order_date" label="Date" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" /></td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-400 text-sm">
                  No orders found. Click "Sync Orders" to pull from Keap.
                </td></tr>
              ) : orders.map(o => (
                <React.Fragment key={o.id}>
                  <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleDetail(o.id)}>
                    <td className="px-4 py-3 text-gray-400">
                      {expandedId === o.id ? <ChevronDown className="h-4 w-4" /> : <ChevronR className="h-4 w-4" />}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm text-gray-900">#{o.keap_order_id}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-gray-500">{o.keap_contact_id || '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{o.contact_full_name || '—'}</div>
                      <div className="text-xs text-gray-400">{o.contact_email || ''}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        o.order_type === 'club' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                      }`}>{o.order_type === 'club' ? 'Club' : 'Trade-In'}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">{fmtCurrency(o.total_paid)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{o.merchant_account_name || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <MatchBadge method={o.transaction_match_method} confidence={o.transaction_match_confidence} linked={!!o.linked_transaction_id} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <MatchBadge method={o.callaway_match_method} confidence={o.callaway_match_confidence} linked={!!o.linked_callaway_invoice_id} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{fmtDate(o.order_date)}</td>
                  </tr>
                  {expandedId === o.id && (
                    <tr>
                      <td colSpan={10} className="px-8 py-4 bg-gray-50">
                        {detailLoading ? (
                          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                        ) : detail ? (
                          <OrderDetailPanel detail={detail} />
                        ) : null}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex gap-2">
              <button onClick={() => loadOrders(pagination.page - 1)} disabled={pagination.page <= 1}
                className="p-1.5 rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button onClick={() => loadOrders(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages}
                className="p-1.5 rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const OrderDetailPanel: React.FC<{ detail: KeapOrderDetail }> = ({ detail }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    {/* Contact & Shipping */}
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <h4 className="text-xs font-medium text-gray-500 uppercase mb-2 flex items-center gap-1"><Truck className="h-3.5 w-3.5" /> Shipping</h4>
      <p className="text-sm font-medium text-gray-900">{detail.contact_full_name}</p>
      {detail.shipping_street1 && <p className="text-xs text-gray-500">{detail.shipping_street1}</p>}
      {detail.shipping_city && <p className="text-xs text-gray-500">{detail.shipping_city}, {detail.shipping_state} {detail.shipping_zip}</p>}
      {detail.shipping_phone && <p className="text-xs text-gray-500 mt-1">{detail.shipping_phone}</p>}
      {detail.contact_email && <p className="text-xs text-blue-600 mt-1">{detail.contact_email}</p>}
    </div>

    {/* Linked Transaction */}
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <h4 className="text-xs font-medium text-gray-500 uppercase mb-2 flex items-center gap-1"><CreditCard className="h-3.5 w-3.5" /> Transaction</h4>
      {detail.linked_transaction ? (
        <div>
          <p className="text-sm font-medium text-gray-900">{fmtCurrency(detail.linked_transaction.amount)}</p>
          <p className="text-xs text-gray-500">{detail.linked_transaction.platform?.account_name}</p>
          <p className="text-xs text-gray-400">{fmtDate(detail.linked_transaction.created_at)}</p>
          <p className="text-xs text-gray-400 mt-1 truncate">{detail.linked_transaction.description || '—'}</p>
        </div>
      ) : (
        <p className="text-xs text-gray-400">Not linked</p>
      )}
    </div>

    {/* Linked Callaway */}
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <h4 className="text-xs font-medium text-gray-500 uppercase mb-2 flex items-center gap-1"><Package className="h-3.5 w-3.5" /> Callaway Invoice</h4>
      {detail.linked_callaway ? (
        <div>
          <p className="text-sm font-medium text-gray-900">{detail.linked_callaway.invoice_number}</p>
          <p className="text-xs text-gray-500">{fmtCurrency(detail.linked_callaway.amount)} — {detail.linked_callaway.invoice_type}</p>
          <p className="text-xs text-gray-400">{fmtDate(detail.linked_callaway.invoice_date)}</p>
          <p className="text-xs text-gray-400">{detail.linked_callaway.customer_name}</p>
        </div>
      ) : (
        <p className="text-xs text-gray-400">Not linked</p>
      )}
    </div>

    {/* Order Items */}
    {detail.items && detail.items.length > 0 && (
      <div className="col-span-full bg-white rounded-lg border border-gray-200 p-3">
        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Order Items</h4>
        {detail.items.map((item, i) => (
          <div key={i} className="flex items-center justify-between py-1 text-sm">
            <span className="text-gray-700">{item.name || item.product_name || '—'} {item.product_sku && <span className="text-xs text-gray-400">[{item.product_sku}]</span>}</span>
            <span className="text-gray-900 font-medium">{item.quantity} × {fmtCurrency(item.price)}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default KeapOrders;
